import { apiClient } from "@/lib/apiClient";

/** CRM / Lead / Pipeline service — the only place the UI talks to these endpoints. */
export const crmService = {
  // Contacts
  async listContacts(params = {}) {
    const { data } = await apiClient.get("/crm/contacts", { params });
    return data;
  },
  async createContact(payload, force = false) {
    const { data } = await apiClient.post("/crm/contacts", payload, { params: { force } });
    return data; // { created, contact } | { created:false, duplicate }
  },
  async getContact(id) {
    const { data } = await apiClient.get(`/crm/contacts/${id}`);
    return data.contact;
  },
  async updateContact(id, payload) {
    const { data } = await apiClient.put(`/crm/contacts/${id}`, payload);
    return data.contact;
  },
  async archiveContact(id) {
    const { data } = await apiClient.post(`/crm/contacts/${id}/archive`);
    return data;
  },
  async restoreContact(id) {
    const { data } = await apiClient.post(`/crm/contacts/${id}/restore`);
    return data;
  },
  // Notes + activity
  async listNotes(id) {
    const { data } = await apiClient.get(`/crm/contacts/${id}/notes`);
    return data.notes;
  },
  async addNote(id, body) {
    const { data } = await apiClient.post(`/crm/contacts/${id}/notes`, { body });
    return data.note;
  },
  async contactActivity(id) {
    const { data } = await apiClient.get(`/crm/contacts/${id}/activity`);
    return data.activity;
  },
  // Tags
  async listTags() {
    const { data } = await apiClient.get("/crm/tags");
    return data.tags;
  },
  async createTag(name) {
    const { data } = await apiClient.post("/crm/tags", { name });
    return data.tag;
  },
  async deleteTag(id) {
    const { data } = await apiClient.delete(`/crm/tags/${id}`);
    return data;
  },
  // Lead sources
  async listSources() {
    const { data } = await apiClient.get("/crm/lead-sources");
    return data.sources;
  },
  async createSource(name) {
    const { data } = await apiClient.post("/crm/lead-sources", { name });
    return data.source;
  },
  // Pipeline + leads
  async listStages() {
    const { data } = await apiClient.get("/pipeline/stages");
    return data.stages;
  },
  async board() {
    const { data } = await apiClient.get("/pipeline/board");
    return data;
  },
  async listLeads(params = {}) {
    const { data } = await apiClient.get("/leads", { params });
    return data;
  },
  async createLead(payload, force = false) {
    const { data } = await apiClient.post("/leads", payload, { params: { force } });
    return data; // { lead } | { created:false, duplicate }
  },
  async getLead(id) {
    const { data } = await apiClient.get(`/leads/${id}`);
    return data.lead;
  },
  async updateLead(id, payload) {
    const { data } = await apiClient.put(`/leads/${id}`, payload);
    return data.lead;
  },
  async changeStage(id, toStageId) {
    const { data } = await apiClient.post(`/leads/${id}/stage`, { to_stage_id: toStageId });
    return data.lead;
  },
  async leadHistory(id) {
    const { data } = await apiClient.get(`/leads/${id}/history`);
    return data.history;
  },
  // Search
  async search(q) {
    const { data } = await apiClient.get("/search", { params: { q } });
    return data;
  },
  // Transactions
  async listTransactions(params = {}) {
    const { data } = await apiClient.get("/transactions", { params });
    return data;
  },
  async createTransaction(payload) {
    const { data } = await apiClient.post("/transactions", payload);
    return data.transaction;
  },
  async getTransaction(id) {
    const { data } = await apiClient.get(`/transactions/${id}`);
    return data.transaction;
  },
  async updateTransaction(id, payload) {
    const { data } = await apiClient.put(`/transactions/${id}`, payload);
    return data.transaction;
  },
  async closeTransaction(id) {
    const { data } = await apiClient.post(`/transactions/${id}/close`);
    return data.transaction;
  },
  async cancelTransaction(id) {
    const { data } = await apiClient.post(`/transactions/${id}/cancel`);
    return data.transaction;
  },
  async saveCommission(id, payload) {
    const { data } = await apiClient.put(`/transactions/${id}/commission`, payload);
    return data.commission;
  },
  async markCommissionPaid(id, payload = {}) {
    const { data } = await apiClient.post(`/transactions/${id}/commission/mark-paid`, payload);
    return data.commission;
  },
  async listCommissions(params = {}) {
    const { data } = await apiClient.get("/commissions", { params });
    return data;
  },
  // Follow-ups
  async listFollowups(params = {}) {
    const { data } = await apiClient.get("/followups", { params });
    return data;
  },
  async followupSummary() {
    const { data } = await apiClient.get("/followups/summary");
    return data;
  },
  async followupAttention() {
    const { data } = await apiClient.get("/followups/attention");
    return data.items;
  },
  async createFollowup(payload) {
    const { data } = await apiClient.post("/followups", payload);
    return data.followup;
  },
  async completeFollowup(id, connected) {
    const { data } = await apiClient.post(`/followups/${id}/complete`, { connected });
    return data.followup;
  },
  async rescheduleFollowup(id, dueAt) {
    const { data } = await apiClient.post(`/followups/${id}/reschedule`, { due_at: dueAt });
    return data.followup;
  },
  async cancelFollowup(id) {
    const { data } = await apiClient.post(`/followups/${id}/cancel`);
    return data.followup;
  },
};

/** Notify all CRM-aware pages to refetch after a mutation. */
export const emitCrmChanged = () => window.dispatchEvent(new CustomEvent("crm:changed"));
